'use client';

import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { REVEAL_STAGGER, TextReveal } from '@/components/elements/text-reveal';
import { Button } from '@/components/ui/button';
import { appStoreUrl, CONTACT_EMAIL } from '@/lib/constants';
import { cn } from '@/lib/utils';

const GUEST_TIERS: readonly {
  guests: number;
  label: string;
  price: string;
  custom?: boolean;
}[] = [
  { guests: 5, label: '5', price: 'Free' },
  { guests: 10, label: '10', price: '$1.99' },
  { guests: 25, label: '25', price: '$4.99' },
  { guests: 50, label: '50', price: '$14.99' },
  { guests: 100, label: '100', price: '$29.99' },
  { guests: 150, label: '150', price: '$49.99' },
  { guests: 200, label: '200', price: '$69.99' },
  { guests: 201, label: '200+', price: 'Contact us', custom: true },
];

export function Pricing({
  className = 'section-padding',
  // Kept for call-site compatibility; the eyebrow always renders in the redesign.
  showBadge: _showBadge = false,
}: {
  className?: string;
  showBadge?: boolean;
}) {
  const [selectedIndex, setSelectedIndex] = useState(2);
  const reduced = useReducedMotion();

  const prev = GUEST_TIERS[selectedIndex - 1];
  const selected = GUEST_TIERS[selectedIndex];
  const next = GUEST_TIERS[selectedIndex + 1];

  return (
    <section className={cn('relative', className)}>
      {/* Floating photo card */}
      <div
        aria-hidden
        className="pointer-events-none absolute right-[8%] bottom-[4%] hidden rotate-6 lg:block"
      >
        <div className="relative w-[149px] drop-shadow-[0px_8px_9px_rgba(0,0,0,0.14)]">
          <Image
            src="/images/hero/cards/beach-day.jpg"
            alt=""
            width={298}
            height={228}
            className="h-[114px] w-[149px] rounded-[19px] object-cover"
          />
          <span className="bg-secondary text-secondary-foreground absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 text-[11px] whitespace-nowrap">
            🏖️ Beach day
          </span>
        </div>
      </div>

      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
          className="text-center"
        >
          {/* Header */}
          <TextReveal mode="animate">
            <span className="text-text-tertiary font-mono text-xs font-medium tracking-[3px] uppercase">
              Pricing
            </span>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER} className="mt-5">
            <h1 className="text-4xl tracking-tight md:text-5xl lg:text-[64px]">
              One event, <span className="text-text-tertiary">one price.</span>
            </h1>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER * 2} className="mt-6">
            <p className="text-muted-foreground mx-auto max-w-[560px] text-[17px] leading-[27px]">
              Free up to 5 guests. After that, one flat charge that scales with
              your guest list — no subscriptions, no monthly fees.
            </p>
          </TextReveal>

          {/* Price dial */}
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="bg-foreground mx-auto h-[18px] w-0.5" />
            <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-baseline gap-6 md:gap-12">
              <span className="font-display text-text-tertiary justify-self-end text-xl opacity-55 md:text-[32px]">
                {prev?.price}
              </span>
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={selected.price}
                  initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduced ? { opacity: 0 } : { opacity: 0, y: -16 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className={cn(
                    'font-display text-foreground leading-none tracking-tight',
                    selected.custom
                      ? 'text-4xl md:text-[64px]'
                      : 'text-6xl md:text-[96px]',
                  )}
                >
                  {selected.price}
                </motion.span>
              </AnimatePresence>
              <span className="font-display text-text-tertiary justify-self-start text-xl opacity-55 md:text-[32px]">
                {next?.price}
              </span>
            </div>

            {/* Guest count chips */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3.5">
              {GUEST_TIERS.map((tier, i) => (
                <button
                  key={tier.guests}
                  type="button"
                  aria-pressed={selectedIndex === i}
                  onClick={() => setSelectedIndex(i)}
                  className={cn(
                    'font-display h-12 w-[68px] cursor-pointer rounded-xl text-lg transition-colors',
                    selectedIndex === i
                      ? 'bg-foreground text-background'
                      : 'bg-surface border-foreground/12 text-foreground hover:border-foreground/30 border',
                  )}
                >
                  {tier.label}
                </button>
              ))}
            </div>

            <p className="text-muted-foreground mt-8 text-[15px]">
              {selected.custom
                ? 'for 200+ guests · weddings, festivals & brands'
                : `for up to ${selected.label} guests · one-time, per event`}
            </p>

            {/* CTA */}
            <Button
              asChild
              className="mx-auto mt-12 h-14 w-full max-w-[320px] rounded-xl text-base"
            >
              {selected.custom ? (
                <Link href={`mailto:${CONTACT_EMAIL}`}>Contact us</Link>
              ) : (
                <Link
                  href={appStoreUrl('web_pricing')}
                  target="_blank"
                  rel="noopener"
                >
                  Create an event
                </Link>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
